import 'reactflow/dist/style.css';

import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import TimeAgo from 'react-timeago';
import ReactFlow, {
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
} from 'reactflow';

import { getSitePowerCapacityQuery } from '../../../../lib/Queries';
import DataComponentError from '../../Shared/DataComponentError';
import Loading from '../../Shared/Loading';
import { loadChilds, nodesOrder } from '../Logic/FormatUtils';
import CustomNode from './CustomNode';

const nodeTypes = {
  custom: CustomNode,
};

/**
 * Generates an array of node objects based on the provided data.
 * @param {Array} assemblies - The data to generate the nodes from.
 * @param {Function} setNodes - The function to set the generated nodes.
 */
export const generateNodes = (assemblies, setNodes, setEdges, timezone) => {
  const sortedAssemblies = assemblies.sort((a, b) => {
    const indexA = nodesOrder.indexOf(
      a.scaleos_type || (a.array && a.array[0].scaleos_type.split('_')[0]),
    );
    const indexB = nodesOrder.indexOf(
      b.scaleos_type || (b.array && b.array[0].scaleos_type.split('_')[0]),
    );
    return indexA - indexB;
  });

  let positionX = 20;
  let positionY = 100;
  let loadChildsPosY = sortedAssemblies.length <= 3 ? 100 : sortedAssemblies.length === 4 ? 50 : 10;
  let sourcePos =
    sortedAssemblies.length <= 3
      ? [Position.Right]
      : sortedAssemblies.length === 4
        ? [Position.Top, Position.Bottom]
        : [Position.Top, Position.Right, Position.Bottom];
  let sourceIndex = 0;
  let idItem = 1;
  let dataNodes = [];
  let powerDER = 0;
  let powerUtility = 0;

  /*
   * Nodes position (x, y) and parent/child
   */
  for (const item of sortedAssemblies) {
    let nestedId = 0;
    let loadChildsPosX = positionX;

    if (Array.isArray(item.array)) {
      let isSourcePos = false;
      const isUtilityGroup =
        item?.name === 'Utility' ||
        item?.scaleos_type === 'Utility' ||
        (item.array && (item.array[0]?.scaleos_type || '').toLowerCase() === 'utility');
      let utilityStackPosY = loadChildsPosY;
      for (const nestedItem of item.array) {
        const obj = {
          id: `${idItem.toString()}-${nestedId.toString()}`,
          type: 'custom',
          data: {
            sourcePos: !isSourcePos ? sourcePos[sourceIndex] : 'left',
            nodeType: 'square',
            source: nestedItem.scaleos_type,
            name: nestedItem.scaleos_name.replace('_', ' '),
            power: nestedItem.power,
            percentage: nestedItem.percentage,
            alarms: nestedItem.alarms,
            alarmsCount: nestedItem.alarms_count,
            timezone: timezone,
            ...(nestedItem['soc'] && { soc: nestedItem.soc }),
          },
          position: {
            x: isUtilityGroup ? positionX : loadChildsPosX,
            y: isUtilityGroup ? utilityStackPosY : loadChildsPosY,
          },
          child: loadChilds.includes(nestedItem.scaleos_type),
        };
        isSourcePos = true;
        if (isUtilityGroup) {
          utilityStackPosY += 90;
        } else {
          loadChildsPosX += 80;
        }
        nestedId += 1;
        dataNodes.push(obj);
        if (nestedItem.scaleos_type.toLowerCase() === 'utility') {
          powerUtility += parseFloat(nestedItem.power);
        } else {
          powerDER += parseFloat(nestedItem.power);
        }
      }
      sourceIndex += 1;
      loadChildsPosY += 90;
      if (isUtilityGroup) {
        positionX += 200;
        loadChildsPosX = positionX;
      }
      idItem += 1;
    } else {
      if (loadChilds.includes(item.scaleos_type)) {
        const obj = {
          id: `${idItem.toString()}-${nestedId.toString()}`,
          type: 'custom',
          data: {
            sourcePos: sourcePos[sourceIndex],
            nodeType: 'square',
            source: item.scaleos_type,
            name: item.scaleos_name.replace('_', ' '),
            power: item.power,
            percentage: item.percentage,
            alarms: item.alarms,
            alarmsCount: item.alarms_count,
            timezone: timezone,
            ...(item['soc'] && { soc: item.soc }),
          },
          position: { x: loadChildsPosX, y: loadChildsPosY },
          child: true,
        };
        dataNodes.push(obj);
        loadChildsPosX += 170;
        loadChildsPosY += 90;
        sourceIndex += 1;
        powerDER += parseFloat(item.power);
      } else {
        powerUtility += item.scaleos_type.toLowerCase() === 'utility' ? parseFloat(item.power) : 0;
        const obj = {
          id: `${idItem.toString()}-${nestedId.toString()}`,
          type: 'custom',
          data: {
            sourcePos: 'left',
            nodeType: 'square',
            source: item.scaleos_type,
            name: item.scaleos_name.replace('_', ' '),
            power: item.power,
            percentage: item.percentage,
            alarms: item.alarms,
            alarmsCount: item.alarms_count,
            timezone: timezone,
            ...(item['soc'] && { soc: item.soc }),
          },
          position: { x: positionX, y: positionY },
          child: false,
        };
        positionX += 200;
        loadChildsPosX = positionX;
        dataNodes.push(obj);
      }
      idItem += 1;
    }
  }

  // Re-center DER child rows vertically around the Load node
  const loadNode = dataNodes.find(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === 'load',
  );
  if (loadNode) {
    // preserve original row order by scanning dataNodes in order
    const seenRows = new Set();
    const rowYs = [];
    for (const n of dataNodes) {
      if (n.child) {
        const y = n.position?.y || 0;
        const key = y.toString();
        if (!seenRows.has(key)) {
          seenRows.add(key);
          rowYs.push(y);
        }
      }
    }
    const numRows = rowYs.length;
    if (numRows > 0) {
      const spacing = 90;
      const startY = loadNode.position.y - Math.round((numRows - 1) / 2) * spacing;
      const remap = new Map(rowYs.map((y, i) => [y, startY + i * spacing]));
      for (const n of dataNodes) {
        if (n.child) {
          const oldY = n.position?.y || 0;
          const newY = remap.get(oldY);
          if (typeof newY === 'number') {
            n.position = { ...n.position, y: newY };
          }
        }
      }
    }
  }

  const powerSum = powerUtility + powerDER;
  const porcUtility = Math.min(
    Math.max(parseFloat(((powerUtility / powerSum) * 100).toFixed(0)), 0),
    100,
  );
  const porcDER = Math.min(Math.max(parseFloat(((powerDER / powerSum) * 100).toFixed(0)), 0), 100);

  let newDataNodes = dataNodes
    .map((item) => {
      if (item.data.source.toLowerCase() === 'load') {
        const load = {
          id: `${item.id}-pie`,
          type: 'custom',
          data: {
            sourcePos: 'left',
            percentage: porcDER,
            nodeType: 'piechart',
            source: 'load-pie',
          },
          position: {
            x: item.position.x + 120,
            y: item.position.y + 17,
          },
          child: false,
        };
        return [item, load];
      }
      // utility nodes share a single pie, added below once their extent is known
      return item;
    })
    .flat();

  // Insert a single shared utility pie centered vertically among utility nodes
  const utilitySquares = newDataNodes.filter(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === 'utility',
  );
  if (utilitySquares.length > 0) {
    const minY = Math.min(...utilitySquares.map((n) => n.position.y));
    const maxY = Math.max(...utilitySquares.map((n) => n.position.y));
    const centerY = Math.round(minY + (maxY - minY) / 2);
    // place utility pie 120px to the right of the leftmost utility square
    const minX = Math.min(...utilitySquares.map((n) => n.position.x));
    const utilityPie = {
      id: `${utilitySquares[0].id}-pie-shared`,
      type: 'custom',
      data: {
        sourcePos: 'left',
        percentage: porcUtility,
        nodeType: 'piechart',
        source: 'utility-pie',
      },
      position: {
        x: minX + 120,
        y: centerY + 17,
      },
      child: false,
    };
    // ensure it's after the last utility square for edge ordering simplicity
    newDataNodes.push(utilityPie);
  }

  const utilityPieNode = newDataNodes.find(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === 'utility-pie',
  );
  const loadPieNode = newDataNodes.find(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === 'load-pie',
  );
  const loadSquareNode = newDataNodes.find(
    (n) => n.data?.source?.toLowerCase && n.data.source.toLowerCase() === 'load',
  );

  const dataEdges = [];
  const edgeStyle = { stroke: '#868686', strokeDasharray: '2 2' };
  const addEdge = (source, target) =>
    dataEdges.push({
      id: `e${source}_${target}`,
      source,
      target,
      type: 'smoothstep',
      style: edgeStyle,
    });

  // Utility edges: each utility square -> shared utility pie, then shared pie -> load pie
  if (utilityPieNode) {
    for (const u of utilitySquares) {
      addEdge(u.id, utilityPieNode.id);
    }
    if (loadPieNode) {
      addEdge(utilityPieNode.id, loadPieNode.id);
    }
  }

  if (loadSquareNode && loadPieNode) {
    addEdge(loadSquareNode.id, loadPieNode.id);
  }

  // DER rows: connect like-type nodes within the same row (same y), and row start -> load pie
  const normalizeType = (s) => s.replace(/ \d+$/, '').toLowerCase();
  const rows = new Map();
  for (const n of newDataNodes.filter((n) => n.child === true)) {
    const key = `${normalizeType(n.data.source)}_${n.position.y}`;
    if (!rows.has(key)) rows.set(key, []);
    rows.get(key).push(n);
  }
  for (const nodesOfRow of rows.values()) {
    nodesOfRow.sort((a, b) => a.position.x - b.position.x);
    for (let i = 0; i < nodesOfRow.length - 1; i++) {
      addEdge(nodesOfRow[i].id, nodesOfRow[i + 1].id);
    }
    if (loadPieNode) {
      addEdge(loadPieNode.id, nodesOfRow[0].id);
    }
  }

  setEdges(dataEdges);
  setNodes(newDataNodes);
};

const SiteDiagram = ({ site, flags }) => {
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);

  const { isLoading, error, data } = useQuery({ ...getSitePowerCapacityQuery(site.uuid) });

  useEffect(() => {
    if (data && data.assemblies.length > 0) {
      generateNodes(data.assemblies, setNodes, setEdges, site?.timezone);
    } else {
      setNodes([]);
      setEdges([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isLoading)
    return (
      <div className="site-diagram">
        <Box display="flex" flexDirection="row" alignItems="baseline">
          <Box>
            <Typography variant="h4" color="secondary">
              Site Overview
            </Typography>
          </Box>
        </Box>
        <Loading />
      </div>
    );
  if (error)
    return (
      <div className="site-diagram">
        <Box display="flex" flexDirection="row" alignItems="baseline">
          <Box>
            <Typography variant="h4" color="secondary">
              Site Overview
            </Typography>
          </Box>
        </Box>
        <DataComponentError />
      </div>
    );
  if (data) {
    return (
      <Box className="site-diagram" sx={{ width: '100%', height: '390px' }}>
        <Box display="flex" flexDirection="row" alignItems="baseline">
          <Box>
            <Typography variant="body2" color="secondary">
              Site Overview
            </Typography>
          </Box>
          <Box pl="2em">
            <Typography variant="subtitle1" color="grey">
              <TimeAgo date={data.last_update[0] + ' UTC'} />
            </Typography>
          </Box>
        </Box>
        <ReactFlowProvider>
          <FlowChild nodes={nodes} edges={edges} />
        </ReactFlowProvider>
      </Box>
    );
  }
};

const FlowChild = (nodes, edges) => {
  const snapGrid = [10, 10];
  const [flowInstance, setFlowInstance] = useState(null);
  const nodesInitialized = useNodesInitialized();
  useEffect(() => {
    if (flowInstance && nodesInitialized) {
      flowInstance.fitView({ minZoom: 1, maxZoom: 1 });
    }
  }, [flowInstance, nodesInitialized]);

  if (nodes?.nodes.length > 0) {
    return (
      <ReactFlow
        className="grey-background"
        style={{ top: '-40px' }}
        nodes={nodes.nodes}
        edges={nodes.edges}
        snapToGrid={true}
        snapGrid={snapGrid}
        nodeTypes={nodeTypes}
        elementsSelectable={false}
        nodesConnectable={false}
        nodesDraggable={false}
        zoomOnScroll={false}
        panOnScroll={false}
        panOnScrollMode={false}
        zoomOnDoubleClick={false}
        panOnDrag={false}
        zoomOnPinch={false}
        preventScrolling={false}
        onInit={setFlowInstance}
        onNodeMouseEnter={() => {}}
      />
    );
  }
};

SiteDiagram.propTypes = {
  site: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  flags: PropTypes.object,
};

export default SiteDiagram;
